import React, { useCallback, useId, useRef } from 'react';
import type { RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useFormContext } from 'react-hook-form';
import { UseEditorOptions } from '@wix/da-editor-tiptap/pkg/hooks/useEditor';
import FormElement from '@wix/da-ds/pkg/formControls/layouts/FormElement';
import InputLabelText from '@wix/da-ds/pkg/formControls/lego/InputLabelText';
import InputErrorMessage from '@wix/da-ds/pkg/formControls/lego/InputErrorMessage';
import OriginalsQuestionEditor from '../../../../common/OriginalsQuestionEditor';
import { QuestionFieldName } from '../../types';
import { EditorTiptapContentReadyHandler } from '@wix/da-shared-react/pkg/DescriptionEditor/DescriptionEditor';
import { EditorApi } from '@wix/da-editor-tiptap/pkg/components/Editor/types';

interface Props {
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
}

const QuestionField: React.FC<Props> = ({ scrollContainerRef }) => {
  const { t } = useTranslation();
  const id = useId();
  const { control, errors, setValue, getValues, clearErrors } = useFormContext();
  const {
    field: { value },
  } = useController({
    name: QuestionFieldName.QUESTION_BODY,
    control,
  });
  const error = errors[QuestionFieldName.QUESTION_BODY];

  // As generating JSON is expensive for every typing event, we lazily resolve
  // the editors save state on submit rather than on typing.
  const lazyEditorStateValueResolver = useRef<() => string>(undefined);
  const handleInitialContentReady: EditorTiptapContentReadyHandler = useCallback(
    (api: EditorApi) => {
      lazyEditorStateValueResolver.current = () => {
        return JSON.stringify(api.getStateForSave());
      };
      setValue(QuestionFieldName.QUESTION_BODY, lazyEditorStateValueResolver.current, {
        shouldDirty: false,
      });
    },
    [setValue]
  );

  const handleChange: UseEditorOptions['onEditorChange'] = useCallback(
    editorState => {
      clearErrors(QuestionFieldName.QUESTION_BODY);
      setValue(QuestionFieldName.QUESTION_BODY, getValues(QuestionFieldName.QUESTION_BODY), {
        shouldDirty: true,
      });
    },
    [clearErrors, getValues, setValue]
  );

  return (
    <FormElement
      inputId={id}
      label={
        <InputLabelText isRequired>
          {t('originalDeviationPopup.questionnaire.form.question.label')}
        </InputLabelText>
      }
    >
      <OriginalsQuestionEditor
        scrollContainerRef={scrollContainerRef}
        placeholder={t('originalDeviationPopup.questionnaire.form.question.placeholder')}
        initialContent={value}
        onInitialContentReady={handleInitialContentReady}
        onChange={handleChange}
        hasError={!!error}
      />
      {error && <InputErrorMessage>{error.message}</InputErrorMessage>}
    </FormElement>
  );
};

export default QuestionField;
